import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { KeyRound, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { UserSaveFooter } from './UserSaveFooter';
import {
  CreatePasskeyRegistrationLinkDoc,
  RevokePasskeyDoc,
  SetUserRolesDoc,
} from './userDocuments';
import type { UserForm, UserRecord } from './useUserForm';

export interface PasskeyLink {
  url: string;
  qrSvg: string;
  expiresAt: string;
}

export interface UserAccessTabProps {
  user: UserRecord;
  form: UserForm;
  scopeOptions: { value: string; label: string }[];
  rolesCatalog: { id: string; name: string; slug: string }[];
  onCancel: () => void;
  onRefetch: () => Promise<unknown>;
  onPasskeyLink: (link: PasskeyLink) => void;
}

/** What this person may reach and how they sign in: scopes, roles, passkeys. */
export function UserAccessTab({
  user,
  form,
  scopeOptions,
  rolesCatalog,
  onCancel,
  onRefetch,
  onPasskeyLink,
}: UserAccessTabProps) {
  const { values, set } = form;
  const [setUserRoles, { loading: savingRoles }] = useMutation(SetUserRolesDoc);
  const [revokePasskey, { loading: revoking }] = useMutation(RevokePasskeyDoc);
  const [createPasskeyLink, { loading: creatingLink }] = useMutation(
    CreatePasskeyRegistrationLinkDoc,
  );
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <FormLayout
        onSubmit={form.submit}
        footer={<UserSaveFooter saving={form.saving} onCancel={onCancel} />}
      >
        <FormSection
          title="Access"
          description="Scopes this user can reach. Prefer ACL role bindings below. Admin type follows the Admin / Super Admin role; removing every staff role makes them a customer."
        >
          <FormField label="Allowed scopes">
            <select
              multiple
              value={values.allowedScopeIds}
              onChange={(event) =>
                set(
                  'allowedScopeIds',
                  Array.from(
                    event.target.selectedOptions,
                    (option) => option.value,
                  ),
                )
              }
              className="h-40 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            >
              {scopeOptions.map((scope) => (
                <option key={scope.value} value={scope.value}>
                  {scope.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Default scope">
            <Select
              value={values.defaultScopeId}
              onChange={(event) => set('defaultScopeId', event.target.value)}
              options={[
                { value: '', label: 'No default' },
                ...scopeOptions.filter((scope) =>
                  values.allowedScopeIds.includes(scope.value),
                ),
              ]}
            />
          </FormField>
          <FormField label="Default landing page">
            <Input
              value={values.defaultPage}
              onChange={(event) => set('defaultPage', event.target.value)}
              placeholder="/ or /orders"
            />
          </FormField>
        </FormSection>
      </FormLayout>

      <UserRoleBindingsEditor
        scopes={user.allowedScopes}
        bindings={user.roleBindings ?? []}
        catalog={rolesCatalog}
        saving={savingRoles}
        onSave={async (bindings) => {
          await setUserRoles({ variables: { userId: user.id, bindings } });
          await onRefetch();
        }}
      />

      <Card
        title="Passkeys"
        subtitle="Hardware keys and platform authenticators linked to this account."
        actions={
          <Button
            type="button"
            leftIcon={<KeyRound className="h-4 w-4" />}
            loading={creatingLink}
            onClick={async () => {
              setPasskeyError(null);
              try {
                const result = await createPasskeyLink({ variables: { userId: user.id } });
                const link = result.data?.createPasskeyRegistrationLink;
                if (!link) {
                  setPasskeyError('Could not create a registration link.');
                  return;
                }
                onPasskeyLink(link);
              } catch (err) {
                setPasskeyError(graphqlErrorMessage(err));
              }
            }}
          >
            Generate registration link
          </Button>
        }
      >
        {passkeyError ? (
          <p className="mb-3 text-sm text-red-600 dark:text-red-400">{passkeyError}</p>
        ) : null}
        {user.passkeys.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No passkeys registered yet.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-800">
            {user.passkeys.map((passkey) => (
              <li
                key={passkey.id}
                className="flex items-center justify-between py-3 text-sm"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {passkey.deviceType ?? 'Authenticator'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Registered{' '}
                    {new Date(passkey.createdAt).toLocaleDateString()} · Last
                    used{' '}
                    {passkey.lastUsedAt
                      ? new Date(passkey.lastUsedAt).toLocaleString()
                      : 'never'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2 className="h-4 w-4 text-red-500" />}
                  disabled={revoking}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Revoke this passkey? The user will lose access from this device.',
                      )
                    )
                      return;
                    setPasskeyError(null);
                    try {
                      await revokePasskey({ variables: { id: passkey.id } });
                      await onRefetch();
                    } catch (err) {
                      setPasskeyError(graphqlErrorMessage(err));
                    }
                  }}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function UserRoleBindingsEditor({
  scopes,
  bindings,
  catalog,
  saving,
  onSave,
}: {
  scopes: { id: string; name: string }[];
  bindings: {
    role: { id: string; name: string; slug: string };
    scope: { id: string; name: string };
  }[];
  catalog: { id: string; name: string; slug: string }[];
  saving: boolean;
  onSave: (
    bindings: { scopeId: string; roleId: string | null }[],
  ) => Promise<void>;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const s of scopes) map[s.id] = '';
    for (const b of bindings) {
      map[b.scope.id] = b.role.id;
    }
    return map;
  });
  const scopeKey = scopes.map((s) => s.id).join(',');
  const bindingKey = bindings
    .map((b) => `${b.scope.id}:${b.role.id}`)
    .join(',');
  const [seededKey, setSeededKey] = useState(`${scopeKey}|${bindingKey}`);
  const nextKey = `${scopeKey}|${bindingKey}`;
  if (nextKey !== seededKey) {
    setSeededKey(nextKey);
    const map: Record<string, string> = {};
    for (const s of scopes) map[s.id] = '';
    for (const b of bindings) {
      map[b.scope.id] = b.role.id;
    }
    setDraft(map);
  }

  // Post only the scopes the admin actually changed. Sending every listed scope
  // would include venues this admin cannot manage, and Go refuses the whole save.
  const saved: Record<string, string> = {};
  for (const b of bindings) saved[b.scope.id] = b.role.id;
  const changed = Object.entries(draft)
    .filter(([scopeId, roleId]) => roleId !== (saved[scopeId] ?? ''))
    .map(([scopeId, roleId]) => ({ scopeId, roleId: roleId || null }));

  return (
    <Card
      title="Roles"
      subtitle="Assign one role per scope to grant permissions."
    >
      <div className="space-y-4">
        {scopes.map((scope) => (
          <FormField key={scope.id} label={scope.name}>
            <Select
              value={draft[scope.id] ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  [scope.id]: event.target.value,
                }))
              }
              options={[
                { value: '', label: 'No role' },
                ...catalog.map((role) => ({
                  value: role.id,
                  label: role.name,
                })),
              ]}
            />
          </FormField>
        ))}
        <div className="flex justify-end">
          <Button
            type="button"
            disabled={saving || changed.length === 0}
            onClick={() => onSave(changed)}
          >
            {saving ? 'Saving…' : 'Save role bindings'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
